import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import SearchNavIcon from './icons/SearchNavIcon';
import PersonNavIcon from './icons/PersonNavIcon';
import CenterNavIcon from './icons/CenterNavIcon';
import QrNavIcon from './icons/QrNavIcon';
import SpecialOfferBanner from './SpecialOfferBanner';

// Constants
const drawerWidth = 240;
const NAV_HEIGHT = 80;         // フッター高さ（管理フロントと統一）

// SVGから抽出したカラー定義
const COLORS = {
  navBackground: '#ffffff',
  navBorder: '#e0dfdc',       // cls-3: separator line
  activeBlue: '#0071e3',      // cls-4 / cls-25: active color
  inactiveGray: '#898989',    // cls-32: inactive icon/text color
};

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  backgroundColor: 'white',
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// Component interfaces
interface LayoutProps {
  children: React.ReactNode;
  mode: PaletteMode;
  toggleColorMode: () => void;
}

// ============================================================
// ナビゲーション設定 (4ボタン構成)
// ============================================================
const NAV_ITEMS = [
  { id: 'jobs',       label: '求人を探す', path: '/jobs',       icon: SearchNavIcon, size: 26 },
  { id: 'scout',      label: '特別オファー', path: '/scout',    icon: CenterNavIcon, size: 40 },
  { id: 'attendance', label: '出勤予定表', path: '/attendance', icon: QrNavIcon,     size: 26 },
  { id: 'mypage',     label: 'マイページ', path: '/mypage',     icon: PersonNavIcon, size: 29 },
] as const;

// Main component
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const open = false;
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {/* ヘッダー */}
      <AppBar position="fixed" open={open} elevation={0}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          minHeight: '80px',
        }}>
          <img
            src="/recrew_logo_A-3.png"
            alt="Re:crew Logo"
            style={{
              height: 34,
              maxWidth: '180px',
              objectFit: 'contain'
            }}
          />
        </Box>
      </AppBar>

      {/* メインコンテンツ */}
      <Box component="main" sx={{
        flexGrow: 1,
        mt: '80px',
        bgcolor: '#f8f8f9',
        pb: `${NAV_HEIGHT}px`,
      }}>
        {/* 特別オファー到着バナー: 未応答オファーがある間は全画面の上部に表示（修正依頼 #49） */}
        <SpecialOfferBanner />
        {children}
      </Box>

      {/* フッターナビゲーション */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          height: `${NAV_HEIGHT}px`,
          backgroundColor: COLORS.navBackground,
          borderTop: `1px solid ${COLORS.navBorder}`,
          boxShadow: '0px -2px 10px rgba(4,0,0,0.12)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          // iOS safe area対応 + 端末下端から少し余白を取りアイコン/文字を上方向にオフセット
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        }}
      >
        {NAV_ITEMS.map((item) => {
          const active = isActive(item.path);
          const Icon = item.icon as React.FC<{ size?: number; color?: string }>;
          return (
            <Box
              key={item.id}
              component={RouterLink}
              to={item.path}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                textDecoration: 'none',
                color: active ? COLORS.activeBlue : COLORS.inactiveGray,
              }}
            >
              <Icon size={item.size} color={active ? COLORS.activeBlue : COLORS.inactiveGray} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  lineHeight: 1,
                  color: 'inherit',
                  fontWeight: active ? 600 : 400,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default Layout;
